import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import {Links} from '@yoroi/types'
import * as React from 'react'

import {LargeYSpace, SmallYSpace} from '../common/space'
import {
  PartnerInfo,
  filterPartnerInfo,
  usePartnerInfo,
} from '../common/partner-info'
import {Dialog} from 'common/Dialog'
import {requestAda} from 'api/requestAda'

export const TransferRequestAda = () => {
  const [scheme, setScheme] = React.useState<
    Links.YoroiUriConfig['scheme'] | ''
  >('')
  const [exchangeLink, setExchangeLink] = React.useState('')
  const [memo, setMemo] = React.useState('')
  const [receivers, setReceivers] = React.useState<Receivers>([])
  const [amounts, setAmounts] = React.useState<Amounts>([])

  const [schemaError, setSchemeError] = React.useState(false)
  const [targetsError, setTargetsError] = React.useState(false)
  const [linkError, setLinkError] = React.useState(false)

  const partnerInfo = usePartnerInfo()

  const noSchema = scheme === ''
  const noReceivers = receivers.length === 0
  const noAmounts =
    amounts.length === 0 ||
    receivers.some(
      (receiver) =>
        amounts.filter((amount) => receiver === amount.receiver).length === 0,
    )

  const disabled = noSchema || noReceivers || noAmounts

  const onGenerateLink = React.useCallback(() => {
    if (noSchema) setSchemeError(true)
    if (noReceivers) setTargetsError(true)
    if (noAmounts) setTargetsError(true)
    if (disabled) return

    const targets = receivers.map((receiver) => {
      const receiverAmounts = amounts
        .filter((amount) => amount.receiver === receiver)
        .map(({datum, ...rest}) => ({
          ...rest,
          ...(datum !== '' ? {datum} : {}),
        }))

      return {receiver, amounts: receiverAmounts}
    })

    const filteredPartnerInfo = filterPartnerInfo(partnerInfo)

    const {link, error} = requestAda({
      scheme,
      targets,
      memo,
      partnerInfo: filteredPartnerInfo,
    })

    if (link && !error) {
      if (linkError) setLinkError(false)
      setExchangeLink(link)
      return
    }

    setLinkError(true)
  }, [
    noSchema,
    noReceivers,
    noAmounts,
    disabled,
    receivers,
    partnerInfo,
    scheme,
    memo,
    amounts,
    linkError,
  ])

  return (
    <Box sx={{width: '100%', display: 'flex', flexDirection: 'column', p: 1}}>
      <LargeYSpace />

      <FormControl>
        <InputLabel id="scheme-label">Schema</InputLabel>

        <Select
          sx={{width: '200px'}}
          labelId="scheme-label"
          id="scheme"
          value={scheme}
          label="Schema"
          variant="outlined"
          onChange={(event) => {
            setScheme(event.target.value as Links.YoroiUriConfig['scheme'])
            setSchemeError(false)
          }}
          error={schemaError}
        >
          <MenuItem value="yoroi">yoroi</MenuItem>

          <MenuItem value="https">https</MenuItem>
        </Select>
      </FormControl>

      <LargeYSpace />

      <Targets
        setAmounts={setAmounts}
        setReceivers={setReceivers}
        receivers={receivers}
        amounts={amounts}
        error={targetsError}
      />

      <LargeYSpace />

      <TextField
        onChange={(event) => {
          setMemo(event.target.value)
        }}
        sx={{width: '200px'}}
        id="memo"
        label="Memo (optional"
        variant="outlined"
        value={memo}
      />

      <LargeYSpace />

      <PartnerInfo {...partnerInfo} />

      <LargeYSpace />

      <Button
        onClick={onGenerateLink}
        sx={{width: '150px'}}
        variant="contained"
        disabled={disabled}
      >
        Generate Link
      </Button>

      <LargeYSpace />

      <Divider />

      <LargeYSpace />

      <Typography sx={{fontSize: 18, fontWeight: 600}}>Link:</Typography>

      {linkError ? (
        <Typography sx={{color: 'red'}}>Error generating link</Typography>
      ) : (
        <Typography sx={{wordBreak: 'break-word'}}>{exchangeLink}</Typography>
      )}

      <LargeYSpace />

      <Button
        onClick={() => {
          navigator.clipboard.writeText(exchangeLink)
        }}
        sx={{width: '150px'}}
        variant="contained"
        disabled={exchangeLink === ''}
      >
        Copy
      </Button>

      <LargeYSpace />

      <Button
        onClick={() => {
          window.location = exchangeLink as unknown as Location
        }}
        sx={{width: '150px'}}
        variant="contained"
        disabled={exchangeLink === ''}
      >
        Open
      </Button>
    </Box>
  )
}

type Amount = {
  receiver: string
  tokenId: string
  quantity: string
  datum: string
}
type Amounts = Array<Amount>
type Receivers = Array<string>

const Targets = ({
  receivers,
  amounts,
  setAmounts,
  setReceivers,
}: /* error, */
{
  receivers: Receivers
  amounts: Amounts
  setAmounts: (amounts: Amounts) => void
  setReceivers: (receivers: Receivers) => void
  error: boolean
}) => {
  const [receiverToAddToken, setReceiverToAddToken] = React.useState('')
  const [isAddReceiverDialogOpen, setIsReceiverDialogOpen] =
    React.useState(false)

  const addAmount = (
    receiver: Amount['receiver'],
    tokenId: Amount['tokenId'],
    quantity: Amount['quantity'],
    datum: Amount['datum'],
  ) => {
    const newAmounts = [...amounts, {receiver, tokenId, quantity, datum}]
    setAmounts(newAmounts)
  }
  const removeAmount = (amountToRemove: Amount) => {
    const newAmounts = amounts.filter(
      (amount) => amount.tokenId !== amountToRemove.tokenId,
    )
    setAmounts(newAmounts)
  }

  const addReceiver = (receiver: string) => {
    const newReceivers = [...receivers, receiver]
    setReceivers(newReceivers)
  }

  return (
    <>
      <AddReceiverDialog
        onSubmit={(receiver) => {
          addReceiver(receiver)
          setIsReceiverDialogOpen(false)
        }}
        open={isAddReceiverDialogOpen}
        onClose={() => setIsReceiverDialogOpen(false)}
      />

      <AddTokenDialog
        onSubmit={(tokenId, quantity, datum) => {
          addAmount(receiverToAddToken, tokenId, quantity, datum)
          setReceiverToAddToken('')
        }}
        open={receiverToAddToken !== ''}
        onClose={() => setReceiverToAddToken('')}
      />

      <Button
        sx={{width: '150px'}}
        variant="outlined"
        onClick={() => setIsReceiverDialogOpen(true)}
      >
        Add Receiver
      </Button>

      <SmallYSpace />

      <Box
        sx={{
          borderStyle: 'solid',
          p: 1,
          maxWidth: '100vw',
        }}
      >
        <Box sx={{overflow: 'scroll'}}>
          <TableContainer
            component={Paper}
            sx={{
              width: '100%',
              minHeight: '150px',
              display: 'table',
              tableLayout: 'fixed',
            }}
          >
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Receiver</TableCell>

                  <TableCell></TableCell>

                  <TableCell align="center">Token ID</TableCell>

                  <TableCell align="center">Quantity</TableCell>

                  <TableCell align="center">Datum</TableCell>

                  <TableCell></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {receivers.map((receiver, index) => {
                  const receiverAmounts = amounts.filter(
                    (amount) => amount.receiver === receiver,
                  )

                  const receiverStart = receiver.substring(0, 5)
                  const receiverEnd = receiver.substring(receiver.length - 5)
                  const minimizedReceiver = `${receiverStart}...${receiverEnd}`

                  const space =
                    receiverAmounts.length > 0 ? <SmallYSpace /> : null

                  return (
                    <React.Fragment key={index}>
                      <TableRow
                        key={receiver}
                        sx={{'&:last-child td, &:last-child th': {border: 0}}}
                      >
                        <TableCell component="th" scope="row">
                          <Typography>{minimizedReceiver}</Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Button
                            sx={{width: '150px'}}
                            variant="outlined"
                            onClick={() => setReceiverToAddToken(receiver)}
                          >
                            Add Token
                          </Button>
                        </TableCell>

                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {receiverAmounts.map((amount, index) => (
                              <React.Fragment key={index}>
                                <Typography>{amount.tokenId}</Typography>

                                {space}
                              </React.Fragment>
                            ))}
                          </Box>
                        </TableCell>

                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {receiverAmounts.map((amount, index) => (
                              <React.Fragment key={index}>
                                <Typography>{amount.quantity}</Typography>

                                {space}
                              </React.Fragment>
                            ))}
                          </Box>
                        </TableCell>

                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {receiverAmounts.map((amount, index) => (
                              <React.Fragment key={index}>
                                <Typography key={index}>
                                  {amount.datum === '' ? '-' : amount.datum}
                                </Typography>

                                {space}
                              </React.Fragment>
                            ))}
                          </Box>
                        </TableCell>

                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {receiverAmounts.map((amount, index) => (
                              <React.Fragment key={index}>
                                <Button
                                  color="error"
                                  sx={{width: '100px'}}
                                  variant="outlined"
                                  onClick={() => removeAmount(amount)}
                                >
                                  Remove
                                </Button>

                                {space}
                              </React.Fragment>
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  )
}

const AddTokenDialog = ({
  open,
  onSubmit,
  onClose,
}: {
  open: boolean
  onSubmit: (tokenId: string, quantity: string, datum: string) => void
  onClose: () => void
}) => {
  const [tokenId, setTokenId] = React.useState('')
  const [quantity, setQuantity] = React.useState('')
  const [datum, setDatum] = React.useState('')

  const handleClose = () => {
    setTokenId('')
    setQuantity('')
    setDatum('')
    onClose()
  }

  const handleSubmit = () => {
    onSubmit(tokenId, quantity, datum)
    handleClose()
  }

  return (
    <Dialog onClose={handleClose} title="Add Token" open={open}>
      <Box
        sx={{
          padding: '30px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <TextField
          onChange={(event) => {
            setTokenId(event.target.value)
          }}
          sx={{width: '200px'}}
          id="tokenId"
          label="Token ID"
          variant="outlined"
          value={tokenId}
        />

        <SmallYSpace />

        <TextField
          onChange={(event) => {
            setQuantity(event.target.value)
          }}
          sx={{width: '200px'}}
          id="quantity"
          label="Quantity"
          variant="outlined"
          value={quantity}
        />

        <SmallYSpace />

        <TextField
          onChange={(event) => {
            setDatum(event.target.value)
          }}
          sx={{width: '200px'}}
          id="datum"
          label="Datum (optional)"
          variant="outlined"
          value={datum}
        />

        <SmallYSpace />

        <Button
          onClick={handleSubmit}
          sx={{width: '150px'}}
          variant="contained"
        >
          Add
        </Button>
      </Box>
    </Dialog>
  )
}

const AddReceiverDialog = ({
  open,
  onSubmit,
  onClose,
}: {
  open: boolean
  onSubmit: (receiver: string) => void
  onClose: () => void
}) => {
  const [receiver, setReceiver] = React.useState('')

  const handleClose = () => {
    setReceiver('')
    onClose()
  }

  return (
    <Dialog onClose={handleClose} title="Add Receiver" open={open}>
      <Box
        sx={{
          padding: '30px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <TextField
          onChange={(event) => {
            setReceiver(event.target.value)
          }}
          sx={{width: '200px'}}
          id="receiver"
          label="Receiver"
          variant="outlined"
          value={receiver}
        />

        <SmallYSpace />

        <Button
          onClick={() => onSubmit(receiver)}
          sx={{width: '150px'}}
          variant="contained"
        >
          Add
        </Button>
      </Box>
    </Dialog>
  )
}
